import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { HotelResultCardProps } from '../types/bot.types';
import { AiFillStar } from 'react-icons/ai';
import { Button } from 'antd';
import { IoBed } from 'react-icons/io5';
import SplideGallery from '../../SplideGallery/SplideGallery';
import { formatBotPrice } from './formatBotPrice';
import { formatBotDate } from './formatBotDate';

// Compact card for the horizontal results row — the proportions of the search
// page's small hotel box (`.hotel-box-with-gallery.small`), but with the bot's
// own price fields so the card always shows the price the chat text quotes.
const HotelResultCard: React.FC<HotelResultCardProps> = ({
  name, stars, destination, checkIn, checkOut, nights,
  mealPlan, pricePerNight, totalPrice, currency, freeCancellation, rating, imageUrl, images, onSelect,
}) => {
  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const currentLang = useSelector(
    (s: RootState) => (s.config as any)?.currentLang as string | undefined,
  );

  return (
    <div className="bot-result-card bot-result-card--hotel">
      <div className="bot-result-card__image-wrap">
        {images && images.length > 1
          ? <SplideGallery images={images} arrows />
          : imageUrl
            ? <img src={imageUrl} alt={name} className="bot-result-card__image" />
            : <div className="bot-result-card__image-placeholder"><IoBed size={32} /></div>}
      </div>
      <div className="bot-result-card__body">
        <div className="bot-result-card__name" title={name}>{name}</div>
        <div className="bot-result-card__stars">
          {Array.from({ length: Math.min(stars, 5) }).map((_, i) => <AiFillStar key={i} color="#f5a623" />)}
        </div>
        {destination && <div className="bot-result-card__destination bot-result-card__meta" title={destination}>📍 {destination}</div>}
        {/* Weekdays included so a wrong-weekday date is visible on the card,
            matching the BE-built date line in the chat text. */}
        <div className="bot-result-card__dates">📅 {formatBotDate(checkIn, currentLang)} → {formatBotDate(checkOut, currentLang)} · {nights}{t?.result?.nights ? ` ${t.result.nights}` : 'n'}</div>
        <div className="bot-result-card__badges">
          {mealPlan && <span className="bot-result-card__badge">{mealPlan}</span>}
          {freeCancellation && <span className="bot-result-card__badge bot-result-card__badge--green">{t?.result?.freeCancellation ?? 'Free cancellation'}</span>}
          {rating != null && <span className="bot-result-card__rating">{rating}/10</span>}
        </div>
      </div>
      <div className="bot-result-card__footer">
        <div>
          {pricePerNight !== undefined && (
            <div className="bot-result-card__per-night">{currency} {pricePerNight}{t?.result?.perNight ?? '/night'}</div>
          )}
          <div className="bot-result-card__price">{currency} {formatBotPrice(totalPrice)}</div>
        </div>
        <Button size="small" type="primary" className="btn-style-5" onClick={onSelect}>
          {t?.result?.select ?? 'Select'}
        </Button>
      </div>
    </div>
  );
};

export default HotelResultCard;
